import { format as formatDate, isValid, parseISO } from 'date-fns';
import i18next from 'i18next';
import { defaultDateFormat } from '~/consts/date';

export function formatDateForAPI(date: Date): string {
  return formatDate(date, defaultDateFormat);
}

const formatters: Record<string, Intl.DateTimeFormat> = {};

export function formatDateForDisplay(date: Date): string {
  return formatDate(date, 'P');
}

export function formatDateForRange(date: Date): string {
  const currentLanguage = i18next.resolvedLanguage ?? i18next.language;
  if (!formatters[currentLanguage]) {
    formatters[currentLanguage] = new Intl.DateTimeFormat(currentLanguage, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  }
  const shortFormatter = formatters[currentLanguage];

  return shortFormatter.format(date);
}

export function formatFromISOToDisplay(date: string, format: 'short' | 'mid' | 'long' = 'short'): string {
  const parsed = parseISO(date);
  const valid = isValid(parsed);
  if (!valid) {
    return '—';
  }
  let dateFormat: string;
  switch (format) {
    case 'short':
      dateFormat = 'P';
      break;
    case 'mid':
      dateFormat = 'PP';
      break;
    case 'long':
      dateFormat = 'PPP';
      break;
    default:
      dateFormat = 'P';
  }
  return formatDate(parsed, dateFormat);
}

export function formatFromISOToCalendarDate(date: string, format: 'short' | 'long' = 'short'): string {
  const parsed = parseISO(date);
  const valid = isValid(parsed);
  if (!valid) {
    return '—';
  }
  return formatDate(parsed, format === 'short' ? 'dd/MM' : 'd MMMM');
}

export const formatISOForAPI = (date: string): string => formatDate(parseISO(date), defaultDateFormat);

export const formatISODateTimeForAPI = (date: string): string => parseISO(date).toISOString();

export function formatFromISOToDatetime(date: string): string {
  const parsed = parseISO(date);
  const valid = isValid(parsed);
  if (!valid) {
    return '—';
  }
  return formatDate(parsed, 'PPP | p');
}

export function formatFromISOToTime(date: string): string {
  const parsed = parseISO(date);
  const valid = isValid(parsed);
  if (!valid) {
    return '—';
  }
  return formatDate(parsed, 'p');
}

export function formatFromISOToShortDate(date: string): string {
  const parsed = parseISO(date);
  const valid = isValid(parsed);
  if (!valid) {
    return '—';
  }
  return formatDateForRange(parsed);
}

export function formatFromISOToMonthYear(date: string): string {
  const parsed = parseISO(date);
  const valid = isValid(parsed);
  if (!valid) {
    return '—';
  }
  return formatDate(parsed, 'MM/yyyy');
}
